/* =====================================================================
   OZARKE — PDP HERO V2 (Swatches + Warranty Details) — SPA-safe
   Fixes:
   - On first load via Highway/AJAX navigation: swatches + warranty "Details" don't work
   - Emitting Color label not updating reliably (scoped)
   - Warranty popup toggles the correct checkbox (scoped to the clicked card)
   - Variant selection + price + Shop Pay variant id update
   - Gallery sync only for image-driving options (uses VariantImageMap-* JSON in DOM)
   ===================================================================== */
(function () {
  if (window.__ozarkePdpV2Bound) return;
  window.__ozarkePdpV2Bound = true;

  function normalize(v) {
    return (v == null ? "" : String(v)).trim().toLowerCase();
  }

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }
  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }

  // -----------------------------
  // Swatch background vars (data-swatch-bg -> --swatch-bg + inline fallback)
  // Keeps colors visible even if body class changes during responsive transitions.
  // -----------------------------
  function applySwatchVars(root) {
    var scope = root || document;
    var els = qsa(
      scope,
      ".drawer-items .emt-color[data-swatch-bg], " +
        ".drawer-items .color-option[data-swatch-bg], " +
        ".drawer-items button.option[data-swatch-bg]",
    );

    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var bg = el.getAttribute("data-swatch-bg");
      if (!bg) continue;

      // CSS variable path (preferred)
      if (el.style.getPropertyValue("--swatch-bg") !== bg) {
        el.style.setProperty("--swatch-bg", bg);
      }
      // keep existing hover logic alive
      if (el.style.getPropertyValue("--hover_color") !== bg) {
        el.style.setProperty("--hover_color", bg);
      }

      // Inline fallback (in case theme CSS selector doesn't match at some breakpoints)
      if (!el.style.background || el.style.background === "transparent") {
        el.style.background = bg;
      }
    }
  }
  // Keep swatch colors stable across breakpoint changes (resize/orientation/BFCache)
  (function bindSwatchRepaint() {
    var raf = null;
    function schedule() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        applySwatchVars();
      });
    }
    schedule();
    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("orientationchange", schedule, { passive: true });
    window.addEventListener("pageshow", schedule);
  })();

  // -----------------------------
  // Variants payload parsing
  // -----------------------------
  function parseVariantsPayload(payload) {
    if (!payload) return [];
    // payload format: id||title||price||compare_at||available||img||sku||qty||pre_order_label||pre_order_date|||...
    var parts = String(payload).split("|||");
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var row = parts[i].trim();
      if (!row) continue;
      var cols = row.split("||");
      if (cols.length < 6) continue;
      out.push({
        id: (cols[0] || "").trim(),
        title: (cols[1] || "").trim(),
        price: (cols[2] || "").trim(),
        compareAt: (cols[3] || "").trim(),
        available: (cols[4] || "").trim(), // "true"/"false"
        imageSrc: (cols[5] || "").trim(),
        sku: (cols[6] || "").trim(),
        qty: (cols[7] || "").trim(),
        preorderLabel: (cols[8] || "").trim(),
        preorderDate: (cols[9] || "").trim(),
      });
    }
    return out;
  }

  function readSelections(root) {
    // Read active options by option-position in ascending order
    var groups = qsa(root, ".all-options .option-group[data-option-position]");
    groups.sort(function (a, b) {
      return (
        (parseInt(a.getAttribute("data-option-position"), 10) || 0) -
        (parseInt(b.getAttribute("data-option-position"), 10) || 0)
      );
    });

    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var btn =
        qs(groups[i], ".drawer-items button.option.active") ||
        qs(groups[i], ".drawer-items button.option");
      if (!btn) continue;
      var v = btn.getAttribute("data-value") || (btn.textContent || "").trim();
      values.push(v);
    }
    return values;
  }

  function findMatchingVariant(variants, selectedValues) {
    if (!variants || !variants.length) return null;
    if (!selectedValues || !selectedValues.length) return variants[0] || null;

    for (var i = 0; i < variants.length; i++) {
      var titleParts = (variants[i].title || "").split(" / ");
      var matchCount = 0;
      for (var j = 0; j < selectedValues.length; j++) {
        if (normalize(titleParts[j]) === normalize(selectedValues[j]))
          matchCount++;
        else break;
      }
      if (matchCount === selectedValues.length) return variants[i];
    }
    return null;
  }

  function applyVariantToUI(root, variant) {
    if (!variant) return;

    // Update add-to-cart button variant id
    var atc = qs(root, ".add-to-cart");
    if (atc && variant.id) {
      atc.setAttribute("data-id", variant.id);
      atc.dataset.id = variant.id;
    }

    // Update Shop Pay hidden input (used by accelerated checkout)
    var sp = qs(root, "input.shop-pay-var-id");
    if (sp && variant.id) sp.value = variant.id;

    // Update price + compare-at price (mobile + desktop copies)
    var priceEls = qsa(root, ".price-wrapper .price");
    for (var p = 0; p < priceEls.length; p++) {
      if (variant.price) priceEls[p].textContent = variant.price;
    }

    var compareVal = variant.compareAt;
    var compareWraps = qsa(root, ".price-wrapper .compare-at-price");
    for (var c = 0; c < compareWraps.length; c++) {
      var compareWrap = compareWraps[c];

      if (
        compareVal &&
        compareVal !== "false" &&
        compareVal !== "0" &&
        compareVal !== "₦0.00" &&
        compareVal !== "$0.00"
      ) {
        compareWrap.style.display = "";
        compareWrap.textContent = compareVal;
      } else {
        compareWrap.style.display = "none";
      }
    }

    // Availability UI (minimal)
    if (atc) {
      var isAvail = String(variant.available).toLowerCase() === "true";
      atc.classList.toggle("disabled", !isAvail);

      var inStock = qs(atc, ".in-stock");
      var mobile = qs(atc, ".mobile-fixed");
      var oos = qs(atc, ".out-of-stock");
      var pre = qs(atc, ".pre-order");

      var isPre = variant.preorderLabel && variant.preorderLabel !== "false";
      // If not available => Sold out
      if (!isAvail) {
        if (inStock) inStock.style.display = "none";
        if (mobile) mobile.style.display = "none";
        if (pre) pre.style.display = "none";
        if (oos) oos.style.display = "";
      } else if (isPre) {
        if (oos) oos.style.display = "none";
        if (inStock) inStock.style.display = "none";
        if (mobile) mobile.style.display = "none";
        if (pre) pre.style.display = "";
      } else {
        if (oos) oos.style.display = "none";
        if (pre) pre.style.display = "none";
        if (inStock) inStock.style.display = "";
        if (mobile) mobile.style.display = "";
      }
    }
  }

  // -----------------------------
  // Delivery countdown / estimated arrival (SPA-safe)
  // Fixes blank "order within" text on first load / AJAX navigation
  // -----------------------------
  function initDeliveryCountdown(root) {
    if (!root) return;
    if (root.dataset.ozDeliveryBound === "1") return;

    var wrap = qs(root, ".product-delivery");
    if (!wrap) return;

    root.dataset.ozDeliveryBound = "1";

    var countdownEl = qs(wrap, ".js-cutoff-countdown");
    var arrivalEl = qs(wrap, ".js-arrival-date");

    if (!countdownEl) return;

    function pad(num) {
      return num < 10 ? "0" + num : String(num);
    }

    function readIntAttr(name, fallback) {
      var val = parseInt(wrap.getAttribute(name), 10);
      return isNaN(val) ? fallback : val;
    }

    function formatArrivalDate(totalDays) {
      var d = new Date();
      d.setDate(d.getDate() + totalDays);
      return d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    }

    function update() {
      // keep arrival date populated too, in case anything ever wipes it
      var processingDays = readIntAttr("data-processing-days", 1);
      var transitDays = readIntAttr("data-transit-days", 3);
      var totalDays = processingDays + transitDays;

      if (arrivalEl) {
        var arrivalText =
          (wrap.getAttribute("data-arrival-date") || "").trim() ||
          formatArrivalDate(totalDays);
        arrivalEl.textContent = arrivalText;
      }

      var now = new Date();
      var cutoff = new Date(now.getTime());
      cutoff.setHours(17, 0, 0, 0);

      if (now > cutoff) {
        cutoff.setDate(cutoff.getDate() + 1);
      }

      var diffMs = cutoff - now;
      if (diffMs <= 0) {
        countdownEl.textContent = "0 hrs 00 mins";
        return;
      }

      var diffMinutes = Math.floor(diffMs / 60000);
      var hours = Math.floor(diffMinutes / 60);
      var mins = diffMinutes % 60;

      countdownEl.textContent = hours + " hrs " + pad(mins) + " mins";
    }

    // immediate + a few retries for first-paint / SPA timing issues
    update();
    setTimeout(update, 0);
    setTimeout(update, 120);
    setTimeout(update, 300);

    // keep it fresh, and auto-stop if this root leaves the DOM
    if (root.__ozDeliveryTimer) {
      clearInterval(root.__ozDeliveryTimer);
    }

    root.__ozDeliveryTimer = setInterval(function () {
      if (!document.body || !document.body.contains(root)) {
        clearInterval(root.__ozDeliveryTimer);
        root.__ozDeliveryTimer = null;
        return;
      }
      update();
    }, 30000);
  }

  // -----------------------------
  // Gallery sync (from VariantImageMap-* JSON)
  // -----------------------------
  function initGallerySync(root) {
    if (root.dataset.ozGallerySyncBound === "1") return;
    root.dataset.ozGallerySyncBound = "1";

    // Variant image map may live outside the hero root (in the same section),
    // so we resolve it by section id when possible.
    var mapEl = null;
    var sid = "";
    if (root && root.id && root.id.indexOf("prod-flex-") === 0) {
      sid = root.id.replace("prod-flex-", "");
    }
    var section = root.closest ? root.closest(".shopify-section") : null;
    if (sid) {
      mapEl =
        document.getElementById("VariantImageMap-" + sid) ||
        (section ? section.querySelector("#VariantImageMap-" + sid) : null);
    }
    if (!mapEl) {
      mapEl =
        (section
          ? section.querySelector('script[id^="VariantImageMap-"]')
          : null) || document.querySelector('script[id^="VariantImageMap-"]');
    }
    if (!mapEl) return;

    var variantMap = [];
    try {
      variantMap = JSON.parse(mapEl.textContent || "[]");
    } catch (e) {
      variantMap = [];
    }
    if (!variantMap || !variantMap.length) return;

    function getDots() {
      return qsa(root, ".pdp-hero-slider .dots .dot");
    }
    function getViewport() {
      return qs(root, ".pdp-hero-slider .slides");
    }
    function getTrack() {
      return qs(root, "#product-images");
    }
    function getSlides() {
      return qsa(root, "#product-images .slide");
    }

    function setActiveDot(index) {
      var dots = getDots();
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle("active", i === index);
      }
    }

    function getCurrentTranslateX(el) {
      if (!el) return 0;
      var style = window.getComputedStyle(el);
      var transform = style.transform || style.webkitTransform;
      if (!transform || transform === "none") return 0;
      try {
        var matrix = new DOMMatrixReadOnly(transform);
        return matrix.m41 || 0;
      } catch (e) {
        var match = transform.match(/matrix(3d)?\((.+)\)/);
        if (!match) return 0;
        var values = match[2].split(",").map(function (v) {
          return parseFloat(v.trim());
        });
        return match[1] ? values[12] || 0 : values[4] || 0;
      }
    }

    function forceMoveTrackPrecisely(index) {
      var track = getTrack();
      var slides = getSlides();
      var viewport = getViewport();
      if (!track || !slides[index]) return;
      var slide = slides[index];

      // scroll-based slider
      if (viewport && viewport.scrollWidth > viewport.clientWidth + 2) {
        var left = slide.offsetLeft;
        try {
          viewport.scrollTo({ left: left, behavior: "smooth" });
        } catch (e) {
          viewport.scrollLeft = left;
        }
        return;
      }

      // transform-based slider
      var x = slide.offsetLeft;
      track.style.transform = "translate3d(" + -x + "px, 0, 0)";
    }

    function getActiveDotIndex() {
      var dots = getDots();
      for (var i = 0; i < dots.length; i++) {
        if (dots[i].classList.contains("active")) return i;
      }
      return -1;
    }

    function goToSlide(index) {
      index = parseInt(index, 10);
      if (isNaN(index) || index < 0) return;

      var dots = getDots();
      var track = getTrack();
      var beforeX = getCurrentTranslateX(track);
      var currentIndex = getActiveDotIndex();

      // already on this slide -> do nothing
      if (currentIndex === index) {
        setActiveDot(index);
        return;
      }

      if (dots[index]) {
        try {
          dots[index].click();
        } catch (e) {}
      }

      requestAnimationFrame(function () {
        var afterX = getCurrentTranslateX(track);
        if (Math.abs(afterX - beforeX) < 1) {
          forceMoveTrackPrecisely(index);
        }
        setActiveDot(index);
      });
    }

    function readActiveSelections() {
      var selections = { 1: "", 2: "", 3: "" };
      var groups = qsa(
        root,
        ".all-options .option-group[data-option-position]",
      );
      for (var i = 0; i < groups.length; i++) {
        var pos = parseInt(groups[i].getAttribute("data-option-position"), 10);
        if (!pos) continue;
        var activeBtn =
          qs(groups[i], ".drawer-items button.option.active") ||
          qs(groups[i], ".drawer-items button.option");
        if (!activeBtn) continue;
        selections[pos] =
          activeBtn.getAttribute("data-value") ||
          (activeBtn.textContent || "").trim();
      }
      return selections;
    }

    function getVisualOptionPositions() {
      var positions = [];
      var groups = qsa(
        root,
        ".all-options .option-group[data-option-position]",
      );
      for (var i = 0; i < groups.length; i++) {
        if (
          qs(groups[i], ".drawer-items .color-option, .drawer-items .emt-color")
        ) {
          var pos = parseInt(
            groups[i].getAttribute("data-option-position"),
            10,
          );
          if (pos) positions.push(pos);
        }
      }
      return positions;
    }

    function getImageDriverOptionPositions() {
      var positions = [];
      [1, 2, 3].forEach(function (pos) {
        var valueToImages = {};
        for (var i = 0; i < variantMap.length; i++) {
          var v = variantMap[i];
          var value = v["option" + pos];
          var imageIndex = v.image_index;
          if (value == null || value === "") continue;
          if (imageIndex == null || imageIndex === undefined) continue;
          var key = normalize(value);
          if (!valueToImages[key]) valueToImages[key] = { images: {} };
          valueToImages[key].images[String(imageIndex)] = true;
        }
        var values = Object.keys(valueToImages);
        if (!values.length) return;

        var everyValueMapsToSingle = true;
        var totalDistinctImages = {};
        values.forEach(function (key) {
          var imgs = Object.keys(valueToImages[key].images);
          if (imgs.length !== 1) everyValueMapsToSingle = false;
          imgs.forEach(function (img) {
            totalDistinctImages[img] = true;
          });
        });

        if (
          everyValueMapsToSingle &&
          Object.keys(totalDistinctImages).length > 1
        ) {
          positions.push(pos);
        }
      });
      return positions;
    }

    function findExactVariant(selections) {
      for (var i = 0; i < variantMap.length; i++) {
        var v = variantMap[i];
        var ok1 =
          !selections[1] || normalize(v.option1) === normalize(selections[1]);
        var ok2 =
          !selections[2] || normalize(v.option2) === normalize(selections[2]);
        var ok3 =
          !selections[3] || normalize(v.option3) === normalize(selections[3]);
        if (ok1 && ok2 && ok3) return v;
      }
      return null;
    }

    var imageDriverPositions = getImageDriverOptionPositions();
    if (!imageDriverPositions.length)
      imageDriverPositions = getVisualOptionPositions();

    function findBestImageIndex(selections, clickedBtn) {
      var exact = findExactVariant(selections);
      if (
        exact &&
        exact.image_index !== null &&
        exact.image_index !== undefined
      ) {
        return parseInt(exact.image_index, 10);
      }

      var driverSet = {};
      for (var d = 0; d < imageDriverPositions.length; d++)
        driverSet[imageDriverPositions[d]] = true;

      var best = null;
      var bestScore = -999999;

      for (var i = 0; i < variantMap.length; i++) {
        var v = variantMap[i];
        if (v.image_index === null || v.image_index === undefined) continue;

        var score = 0;
        [1, 2, 3].forEach(function (pos) {
          var selected = selections[pos];
          if (!selected) return;
          var variantVal = v["option" + pos];
          var isMatch = normalize(variantVal) === normalize(selected);
          var isDriver = !!driverSet[pos];
          if (isMatch) score += isDriver ? 100 : 5;
          else score += isDriver ? -1000 : -1;
        });

        if (score > bestScore) {
          bestScore = score;
          best = v;
        }
      }

      if (best && best.image_index !== null && best.image_index !== undefined) {
        return parseInt(best.image_index, 10);
      }

      if (clickedBtn) {
        var fallback = clickedBtn.getAttribute("data-image-index");
        if (fallback != null && fallback !== "") {
          var idx = parseInt(fallback, 10);
          if (!isNaN(idx)) return idx;
        }
      }

      return null;
    }

    function syncGallery(clickedBtn) {
      var selections = readActiveSelections();
      var index = findBestImageIndex(selections, clickedBtn);
      if (index == null || isNaN(index)) return;
      goToSlide(index);
    }

    // Capture so we get it even if other code stops bubbling
    root.addEventListener(
      "click",
      function (e) {
        var btn =
          e.target && e.target.closest
            ? e.target.closest(".drawer-items button.option")
            : null;
        if (!btn) return;

        // Skip only when the swatch handler marked this exact click as a no-op.
        // Do NOT use .active here, because swatch selection runs first and will
        // make newly clicked buttons active before gallery sync sees them.
        if (btn.__ozSkipGalleryOnce) {
          btn.__ozSkipGalleryOnce = false;
          return;
        }

        var clickedPos = parseInt(btn.getAttribute("data-option-position"), 10);
        if (
          imageDriverPositions.length &&
          imageDriverPositions.indexOf(clickedPos) === -1
        ) {
          return; // not an image-driving option
        }

        // Let selection update first, then sync (retry a few times)
        setTimeout(function () {
          syncGallery(btn);
        }, 0);
        setTimeout(function () {
          syncGallery(btn);
        }, 80);
        setTimeout(function () {
          syncGallery(btn);
        }, 180);
      },
      true,
    );
  }

  // -----------------------------
  // Swatches / Options (variant selection)
  // -----------------------------
  function initSwatches(root) {
    if (root.dataset.ozSwatchesBound === "1") return;
    root.dataset.ozSwatchesBound = "1";

    var atc = qs(root, ".add-to-cart");
    var variants = parseVariantsPayload(
      atc ? atc.getAttribute("data-variants") : "",
    );
    root.__ozVariants = variants;

    function getButtonOptionValue(btn) {
      if (!btn) return "";

      var dataValue = btn.getAttribute("data-value");
      if (dataValue && String(dataValue).trim()) {
        return String(dataValue).trim();
      }

      var valEl = qs(btn, ".color-val");
      if (valEl && (valEl.textContent || "").trim()) {
        return (valEl.textContent || "").trim();
      }

      return (btn.textContent || "").trim();
    }

    function syncGroupOptionName(group, explicitBtn) {
      if (!group) return;

      var btn =
        explicitBtn ||
        qs(group, ".drawer-items button.option.active") ||
        qs(group, ".drawer-items button.option");

      if (!btn) return;

      var value = getButtonOptionValue(btn);
      if (!value) return;

      var visibleValue = qs(group, ".option-name .option-selected-value");
      if (visibleValue) visibleValue.textContent = value;
    }

    function syncAllOptionNames() {
      var groups = qsa(
        root,
        ".all-options .option-group[data-option-position]",
      );
      for (var i = 0; i < groups.length; i++) {
        syncGroupOptionName(groups[i]);
      }
    }

    // Ensure swatch colors are painted (SPA-safe)
    applySwatchVars(root);

    // Event delegation: option click
    root.addEventListener(
      "click",
      function (e) {
        var btn =
          e.target && e.target.closest
            ? e.target.closest(".drawer-items button.option")
            : null;
        if (!btn || !root.contains(btn)) return;

        var group = btn.closest(".option-group[data-option-position]");
        if (!group) return;

        // clicking the current active swatch should do absolutely nothing
        // mark this exact click so gallery sync also skips once
        if (btn.classList.contains("active")) {
          btn.__ozSkipGalleryOnce = true;
          setTimeout(function () {
            btn.__ozSkipGalleryOnce = false;
          }, 0);
          return;
        }

        // Keep swatch backgrounds stable
        applySwatchVars(root);

        // set active in group
        var siblings = qsa(group, ".drawer-items button.option");
        for (var i = 0; i < siblings.length; i++)
          siblings[i].classList.remove("active");
        btn.classList.add("active");

        // replace label
        var drawer = btn.closest(".drawer");
        if (drawer && drawer.classList.contains("replace-label")) {
          var current = qs(drawer, ".current-label");
          if (current) current.innerHTML = btn.innerHTML;
        }

        // keep the visible option heading in sync for every option type
        syncGroupOptionName(group, btn);

        // compute selected variant
        var selectedValues = readSelections(root);
        var match = findMatchingVariant(
          root.__ozVariants || [],
          selectedValues,
        );
        applyVariantToUI(root, match);

        // When Shop Pay input changes, Shopify's payment button may need re-init if it doesn't update itself.
        // This is safe to call only if available.
        try {
          if (
            window.Shopify &&
            window.Shopify.PaymentButton &&
            window.Shopify.PaymentButton.init
          ) {
            window.Shopify.PaymentButton.init();
          }
        } catch (err) {}
      },
      true,
    ); // capture to survive stopPropagation from other scripts

    // Initial sync (important for SPA first render)
    var initialValues = readSelections(root);
    var initial = findMatchingVariant(root.__ozVariants || [], initialValues);
    applyVariantToUI(root, initial);

    syncAllOptionNames();
    setTimeout(syncAllOptionNames, 0);
    setTimeout(syncAllOptionNames, 120);
    setTimeout(syncAllOptionNames, 300);
  }

  function bindPopupPortal() {
    if (window.__ozPdpV2PopupPortalBound) return;
    window.__ozPdpV2PopupPortalBound = true;

    function portal() {
      if (!document.body) return;

      document.querySelectorAll(".pop-pdp").forEach(function (p) {
        if (p.dataset.portaled === "1") return;
        p.dataset.portaled = "1";
        document.body.appendChild(p);
      });
    }

    portal();

    try {
      var mo = new MutationObserver(function () {
        portal();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  // -----------------------------
  // Warranty Details popup (scoped)
  // -----------------------------

  function initWarrantyDelegation() {
    // Guard against double-binding (reload vs SPA transitions)
    if (window._ozWarrantyDelegationBound || window.__ozWarrantyDelegationBound)
      return;
    window._ozWarrantyDelegationBound = true;
    window.__ozWarrantyDelegationBound = true;

    var esc =
      window.CSS && window.CSS.escape
        ? window.CSS.escape
        : function (s) {
            return String(s).replace(/["\\]/g, "\\$&");
          };

    function getPopup(variantId, item) {
      if (variantId) {
        var p = document.querySelector(
          '.pop-pdp[data-variant-id="' + esc(variantId) + '"]',
        );
        if (p) return p;
      }
      if (item)
        return document.querySelector(
          '.pop-pdp[data-pdp-item="' + esc(item) + '"]',
        );
      return null;
    }

    function getInput(variantId, item) {
      if (variantId) {
        var i = document.querySelector(
          '.warranty-selector__radio-in[data-variant-id="' +
            esc(variantId) +
            '"]',
        );
        if (i) return i;
      }
      if (item)
        return document.querySelector(
          '.warranty-selector__radio-in[data-pdp-item="' + esc(item) + '"]',
        );
      return null;
    }

    function syncButtonText(popupEl, inputEl) {
      if (!popupEl) return;
      var btn = popupEl.querySelector(".popup-pdp-btn");
      if (!btn) return;

      var type =
        btn.getAttribute("data-pdp-item") ||
        popupEl.getAttribute("data-pdp-item") ||
        "";
      if (!type) return;

      var addSpan = btn.querySelector(".add-" + type);
      var removeSpan = btn.querySelector(".remove-" + type);
      if (!addSpan || !removeSpan) return;

      if (inputEl && inputEl.checked) {
        addSpan.style.display = "none";
        removeSpan.style.display = "inline";
      } else {
        addSpan.style.display = "inline";
        removeSpan.style.display = "none";
      }
    }

    function lockBodyScroll() {
      if (!document.body) return;

      var scrollbarGap =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";

      if (scrollbarGap > 0) {
        document.body.style.paddingRight = scrollbarGap + "px";
      }

      document.body.classList.add("popup-open");
    }

    function unlockBodyScroll() {
      if (!document.body) return;

      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.classList.remove("popup-open");
    }

    function closeAllPopups() {
      document.querySelectorAll(".pop-pdp.view-popup").forEach(function (p) {
        p.classList.remove("view-popup");
      });
      unlockBodyScroll();
    }

    function openPopup(popupEl) {
      if (!popupEl) return;
      closeAllPopups();
      popupEl.classList.add("view-popup");
      lockBodyScroll();
    }

    function closePopup(popupEl) {
      if (!popupEl) return;
      popupEl.classList.remove("view-popup");
      setTimeout(function () {
        if (!document.querySelector(".pop-pdp.view-popup")) {
          unlockBodyScroll();
        }
      }, 300);
    }

    function hardResetPopupState(removePortaled) {
      document.querySelectorAll(".pop-pdp.view-popup").forEach(function (p) {
        p.classList.remove("view-popup");
      });

      unlockBodyScroll();

      if (removePortaled) {
        document
          .querySelectorAll('.pop-pdp[data-portaled="1"]')
          .forEach(function (p) {
            if (p && p.parentNode === document.body) {
              p.parentNode.removeChild(p);
            }
          });
      }
    }

    function bindPopupNavigationCleanup() {
      if (window.__ozPopupNavigationCleanupBound) return;
      window.__ozPopupNavigationCleanupBound = true;

      window.addEventListener(
        "popstate",
        function () {
          hardResetPopupState(true);
        },
        true,
      );

      window.addEventListener("pagehide", function () {
        // Do not remove portaled popups when leaving for Shop Pay / checkout.
        // The page may come back from BFCache, and if we remove them here,
        // warranty / installation details have nothing to open on return.
        hardResetPopupState(false);
      });

      window.addEventListener("pageshow", function () {
        if (!document.querySelector(".pdp-hero.pdp-hero-v2")) {
          hardResetPopupState(true);
        } else {
          bindPopupPortal();
          hardResetPopupState(false);
        }
      });
    }

    function syncExtrasForMainAtc() {
      var all = qsa(document, ".warranty-selector__radio");

      for (var a = 0; a < all.length; a++) {
        var attrs = Array.prototype.slice.call(all[a].attributes || []);
        for (var j = 0; j < attrs.length; j++) {
          if (/^data-variant\d+$/.test(attrs[j].name)) {
            all[a].removeAttribute(attrs[j].name);
          }
        }
      }

      var checked = qsa(document, ".warranty-selector__radio:checked");
      for (var i = 0; i < checked.length; i++) {
        var extraId = checked[i].getAttribute("data-variant-id");
        if (extraId) {
          checked[i].setAttribute("data-variant" + (i + 1), extraId);
        }
      }
    }

    function openFaqPanel(question, panel) {
      var arrow = qs(question, ".arrow");

      question.classList.add("active");
      question.setAttribute("data-state", "open");
      if (arrow) arrow.textContent = "▼";

      panel.classList.add("active");

      // Measure natural height, then animate 0 -> full height
      panel.style.maxHeight = "none";
      var fullHeight = panel.scrollHeight;
      panel.style.maxHeight = "0px";
      panel.offsetHeight; // force reflow for smooth animation
      panel.style.maxHeight = fullHeight + "px";

      // Re-measure after layout settles so text never gets clipped
      requestAnimationFrame(function () {
        if (panel.classList.contains("active")) {
          panel.style.maxHeight = panel.scrollHeight + "px";
        }
      });

      setTimeout(function () {
        if (panel.classList.contains("active")) {
          panel.style.maxHeight = panel.scrollHeight + "px";
        }
      }, 120);

      setTimeout(function () {
        if (panel.classList.contains("active")) {
          panel.style.maxHeight = panel.scrollHeight + "px";
        }
      }, 260);
    }

    function closeFaqPanel(question, panel) {
      var arrow = qs(question, ".arrow");

      question.classList.remove("active");
      question.setAttribute("data-state", "closed");
      if (arrow) arrow.textContent = "►";

      // Animate from current height -> 0
      panel.style.maxHeight = panel.scrollHeight + "px";
      panel.offsetHeight; // force reflow
      panel.classList.remove("active");
      panel.style.maxHeight = "0px";
    }

    function refreshOpenFaqPanels(scope) {
      var panels = qsa(scope || document, ".faq-answer.panel.active");
      for (var i = 0; i < panels.length; i++) {
        panels[i].style.maxHeight = panels[i].scrollHeight + "px";
      }
    }

    function bindFaqAutoResize() {
      if (window.__ozFaqAutoResizeBound) return;
      window.__ozFaqAutoResizeBound = true;

      window.addEventListener(
        "resize",
        function () {
          refreshOpenFaqPanels(document);
        },
        { passive: true },
      );

      window.addEventListener(
        "orientationchange",
        function () {
          refreshOpenFaqPanels(document);
        },
        { passive: true },
      );
    }

    bindFaqAutoResize();

    document.addEventListener(
      "click",
      function (e) {
        var detailsLink =
          e.target && e.target.closest
            ? e.target.closest(".pdp-item-link")
            : null;
        var popupBtn =
          e.target && e.target.closest
            ? e.target.closest(".popup-pdp-btn")
            : null;
        var closeBtn =
          e.target && e.target.closest ? e.target.closest(".pdp-close") : null;
        var overlay =
          e.target && e.target.closest
            ? e.target.closest(".pop-pdp-overlay")
            : null;
        var faqBtn =
          e.target && e.target.closest
            ? e.target.closest(".faq-question.accordion")
            : null;

        // FAQ -> TOGGLE
        if (faqBtn) {
          e.preventDefault();
          e.stopPropagation();

          var question = faqBtn;
          var panel = question.nextElementSibling;
          var container = question.closest(".faq-container");

          if (!panel || !container) return;

          var isOpen = question.classList.contains("active");

          var allQuestions = qsa(container, ".faq-question.accordion");
          for (var q = 0; q < allQuestions.length; q++) {
            var acc = allQuestions[q];
            var accPanel = acc.nextElementSibling;

            if (!accPanel || acc === question) continue;

            acc.classList.remove("active");
            acc.setAttribute("data-state", "closed");

            var accArrow = qs(acc, ".arrow");
            if (accArrow) accArrow.textContent = "►";

            accPanel.classList.remove("active");
            accPanel.style.maxHeight = "0px";
          }

          if (isOpen) {
            closeFaqPanel(question, panel);
          } else {
            openFaqPanel(question, panel);
          }

          return;
        }

        // DETAILS -> OPEN
        if (detailsLink) {
          e.preventDefault();
          e.stopPropagation();

          var variantId = detailsLink.getAttribute("data-variant-id");
          var item = detailsLink.getAttribute("data-pdp-item");

          var popup = getPopup(variantId, item);
          var input = getInput(variantId, item);

          if (popup) {
            syncButtonText(popup, input);
            openPopup(popup);
          }
          return;
        }

        // ADD/REMOVE -> TOGGLE
        if (popupBtn) {
          e.preventDefault();

          var popupEl =
            popupBtn.closest(".pop-pdp") ||
            document.querySelector(".pop-pdp.view-popup");
          var variantId2 =
            popupBtn.getAttribute("data-id") ||
            (popupEl ? popupEl.getAttribute("data-variant-id") : null);
          var item2 =
            popupBtn.getAttribute("data-pdp-item") ||
            (popupEl ? popupEl.getAttribute("data-pdp-item") : null);

          var input2 = getInput(variantId2, item2);
          if (input2) {
            input2.checked = !input2.checked;
            input2.dispatchEvent(new Event("change", { bubbles: true }));
          }

          if (popupEl) {
            syncButtonText(popupEl, input2);
            closePopup(popupEl);
          }
          return;
        }

        // CLOSE
        if (closeBtn) {
          e.preventDefault();
          closePopup(closeBtn.closest(".pop-pdp"));
          return;
        }

        if (overlay) {
          e.preventDefault();
          closePopup(overlay.closest(".pop-pdp"));
        }
      },
      true,
    ); // capture to survive stopPropagation from other scripts

    document.addEventListener(
      "change",
      function (e) {
        var input =
          e.target && e.target.closest
            ? e.target.closest(".warranty-selector__radio-in")
            : null;

        if (!input) return;

        syncExtrasForMainAtc();

        var popup = getPopup(
          input.getAttribute("data-variant-id"),
          input.getAttribute("data-pdp-item"),
        );

        if (popup) {
          syncButtonText(popup, input);
        }
      },
      true,
    );

    syncExtrasForMainAtc();
    bindPopupNavigationCleanup();
  }
  // -----------------------------
  // Init on DOM + SPA navigation (MutationObserver)
  // -----------------------------
  function initAll() {
    bindPopupPortal();
    var roots = qsa(document, ".pdp-hero.pdp-hero-v2");
    for (var i = 0; i < roots.length; i++) {
      initDeliveryCountdown(roots[i]);
      initSwatches(roots[i]);
      initGallerySync(roots[i]);
    }
    initWarrantyDelegation();
  }

  // run now + on ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // observe SPA injections
  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (!mutations[i].addedNodes || !mutations[i].addedNodes.length) continue;
      // lightweight check: if any node contains a pdp hero v2, init
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var n = mutations[i].addedNodes[j];
        if (!n || n.nodeType !== 1) continue;
        if (n.matches && n.matches(".pdp-hero.pdp-hero-v2")) {
          initAll();
          return;
        }
        if (n.querySelector && n.querySelector(".pdp-hero.pdp-hero-v2")) {
          initAll();
          return;
        }
      }
    }
  });
  try {
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}
})();
